import { Space, Tooltip } from "antd";
import GoverningLegend from "components/governing-legend";
import { CiqIconBox } from "components/svg-icons";
import RedirectIcon from "components/svg-icons/redirect-icon";
import { Link, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ViewSelectedItemsDrawer from "pages/bulk-linking/view-selected-items";
import { mergedStrings } from "utils/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { specSectionLabel } from "components/cell-renders";
import { DropdownEntityOption, TEntityLink, TFeatureDetail } from "../models";
import DropDownEntityNames from "./drop-down-entity-names";
import MaterialTrackingCheckbox from "./track-chechbox/material-tracking-chechbox";

type Props = {
  featureDetail: TFeatureDetail;
  selectedEntityIds: string[];
  setSelectedEntityIds: (ids: Array<string>) => void;
  selectedEntityInfo: TEntityLink | undefined;
  setSelectedEntityInfo: React.Dispatch<
    React.SetStateAction<TEntityLink | undefined>
  >;
  refetchEntityInfo: () => Promise<void>;
  disable: boolean;
};

function HeaderTitle(props: Props) {
  const {
    featureDetail,
    selectedEntityIds,
    setSelectedEntityIds,
    selectedEntityInfo,
    setSelectedEntityInfo,
    refetchEntityInfo,
    disable
  } = props;
  const { projectId } = useParams() as any;
  const { state } = useLocation() as { state: any };

  const [showViewSelectedDrawer, setViewSelectedDrawer] = useState(false);

  const [selectedDropdownEntity, setSelectedDropdownEntity] =
    useState<DropdownEntityOption>();

  useEffect(() => {
    if (selectedDropdownEntity)
      setSelectedEntityIds([selectedDropdownEntity?.id]);
  }, [selectedDropdownEntity, setSelectedEntityIds]);

  const bulkHeader = (
    <div className="flex grow items-center px-3 space-x-2">
      <div className="linking-border px-1 py-0.5 text-sm flex w-[467px] space-x-1 rounded items-center">
        <div
          className="font-bold pl-2"
          data-testid="headertitleui-selected-count"
        >
          {state?.selectedRows?.length}
        </div>
        <div className="grow" data-testid="headertitleui-selected-label">
          {featureDetail.titlePlural} Selected from Log
        </div>
        <Space
          className="linking-border rounded p-1 cursor-pointer"
          onClick={() => {
            setViewSelectedDrawer(true);
          }}
          data-testid="headertitleui-button-view"
        >
          View
        </Space>
        <Space
          className="linking-border rounded p-1 cursor-pointer"
          onClick={() => {
            setSelectedEntityIds([]);
            setSelectedEntityInfo(undefined);
          }}
          data-testid="headertitleui-button-clear"
        >
          Clear
        </Space>
      </div>
      {selectedEntityInfo ? (
        <div
          className="flex  items-center"
          data-testid="headertitleui-bulk-tracking-checkbox"
        >
          <MaterialTrackingCheckbox
            selectedEntityInfo={selectedEntityInfo}
            refetchEntityInfo={refetchEntityInfo}
            disable={disable}
          />
        </div>
      ) : null}

      <div
        className="flex items-center space-x-2"
        data-testid="headertitleui-info-section"
      >
        <InfoCircleOutlined />
        <div>
          Links which are not common to all the selected materials are not
          displayed here.
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="w-full px-6 py-2 flex justify-between border-color border-0 border-b"
      data-testid="headertitleui-main-container"
    >
      <div className="flex items-center w-full">
        <div
          className="flex items-center space-x-1"
          data-testid="headertitleui-title-section"
        >
          <CiqIconBox
            feature={featureDetail.feature}
            size={20}
            data-testid="headertitleui-feature-icon"
          />
          <div
            className="text-xl font-semibold pl-1"
            data-testid="headertitleui-title-text"
          >
            {featureDetail.title} Linking
          </div>
        </div>

        {selectedEntityIds.length > 1 ? (
          bulkHeader
        ) : (
          <div className="flex items-center grow">
            <DropDownEntityNames
              featureDetail={featureDetail}
              selectedDropdownEntity={selectedDropdownEntity}
              setSelectedDropdownEntity={setSelectedDropdownEntity}
              disable={disable}
            />

            {selectedEntityInfo ? (
              <div
                className="flex space-x-2 items-center"
                data-testid="headertitleui-entity-actions"
              >
                <Tooltip
                  title={`Open ${featureDetail.title} Detail`}
                  data-testid="headertitleui-tooltip-open-detail"
                >
                  <Link
                    className="flex linking-container--nav__redirectlinkIcon"
                    to={`/project/${projectId}/${featureDetail.titlePlural}/${selectedEntityInfo.id}`}
                    target="_blank"
                    data-testid="headertitleui-link"
                  >
                    <RedirectIcon data-testid="headertitleui-redirect-icon" />
                  </Link>
                </Tooltip>
                <MaterialTrackingCheckbox
                  selectedEntityInfo={selectedEntityInfo}
                  refetchEntityInfo={refetchEntityInfo}
                  disable={disable}
                />
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
      <GoverningLegend data-testid="headertitleui-governing-legend" />
      {showViewSelectedDrawer && (
        <ViewSelectedItemsDrawer
          instruction={`Add more to the selection from the ${featureDetail.title} Log page.`}
          isOpen={showViewSelectedDrawer}
          setOpen={setViewSelectedDrawer}
          items={state?.selectedRows.map((m: any) => {
            const specSection = m.spec_section?.length
              ? m.spec_section
              : {
                  id: "",
                  name: "",
                  number: ""
                };
            return {
              pre: mergedStrings([
                m.sequence_id || "",
                specSectionLabel({
                  specNumber: specSection.number,
                  specName: specSection.name
                })
              ]),
              name: m.title
            };
          })}
          key="ViewSelectedItemsDrawer"
          title={`Selected ${featureDetail.title}`}
          data-testid="headertitleui-view-selected-drawer"
        />
      )}
    </div>
  );
}

export default HeaderTitle;
